import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Grid,
  Paper,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import QuotaStatusCard from './QuotaStatusCard';
import { getMyQuotas } from '../../api/quotas';
import DWLFLoader from '../../components/DWLFLoader';

const QUOTA_ORDER = ['backtest', 'aiAgent', 'evaluation', 'signal'];

const QuotaStatusPanel = () => {
  const [quotas, setQuotas] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchQuotas = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyQuotas();
      // Handle null/undefined API response
      if (!data) {
        setError('No quota data received from server');
        return;
      }
      setQuotas(data.quotas || {});
      setRole(data.role || 'default');
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch quotas:', err);
      setError(err.response?.data?.message || 'Failed to load quota information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotas();
  }, []);

  if (loading && quotas === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Usage Quotas
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Your current plan:
            </Typography>
            <Chip
              label={role === 'admin' ? 'Admin' : role === 'user' ? 'Standard' : 'Basic'}
              size="small"
              color={role === 'admin' ? 'secondary' : 'primary'}
            />
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Updated {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title="Refresh quota status">
          <IconButton onClick={fetchQuotas} disabled={loading}>
            <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={2}>
        {QUOTA_ORDER.map((quotaType) => (
          <Grid key={quotaType} size={{ xs: 12, sm: 6, md: 3 }}>
            <QuotaStatusCard quotaType={quotaType} quotaData={quotas?.[quotaType]} />
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mt: 3, p: 2, backgroundColor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          Quotas reset automatically. Daily limits reset at midnight UTC. Monthly limits reset on the 1st of each month.
          {role !== 'admin' && ' Contact support if you need higher limits.'}
        </Typography>
      </Paper>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default QuotaStatusPanel;
